<template>
	<div class="collaborative-broker-list" :class="{ mobile }">
		<span class="heading">
			{{
				$t(
					brokers.length === 1
						? "opportunities-table.columns.collaborative-broker"
						: "opportunities-table.columns.collaborative-brokers"
				)
			}}
		</span>
		<div v-for="broker in brokers" :key="broker.name" class="broker">
			<div class="identity">
				<img v-if="broker.picture" :src="broker.picture" alt="" class="picture" />
				<div class="text">
					<span class="name">{{ broker.name }}</span>
					<span v-if="broker.title" class="role">{{ $t(broker.title) }}</span>
				</div>
			</div>
			<Popover.Root
				v-if="mobile && contactsOf(broker).length > 0"
				:open="openMenuKey === broker.name"
				:positioning="{ placement: 'bottom-end', gutter: 4 }"
				@open-change="details => (openMenuKey = details.open ? broker.name : null)"
			>
				<Popover.Trigger as-child>
					<Button class="cta" variant="secondary" size="s" type="button">
						{{ $t("opportunities.buttonCTAOffmarket") }}
					</Button>
				</Popover.Trigger>
				<Popover.Positioner>
					<Popover.Content class="contact-menu">
						<a
							v-for="contact in contactsOf(broker)"
							:key="contact.key"
							:href="contact.href"
							class="item"
							@click="handleContact"
						>
							<AppIcon :name="contact.icon" :size="16" :stroke-width="1.2" />
							{{ $t(contact.actionLabel) }}
						</a>
					</Popover.Content>
				</Popover.Positioner>
			</Popover.Root>
			<div v-else-if="!mobile" class="actions">
				<template v-for="contact in contactsOf(broker)" :key="contact.key">
					<Popover.Root
						:open="openMenuKey === contact.key"
						:positioning="{ placement: 'bottom-end', gutter: 4 }"
						@open-change="details => (openMenuKey = details.open ? contact.key : null)"
					>
						<Popover.Trigger as-child>
							<button
								type="button"
								class="action"
								:class="{ copied: copiedKey === contact.key }"
								:title="contact.value"
								:aria-label="$t(contact.actionLabel)"
							>
								<AppIcon
									:name="copiedKey === contact.key ? IconName.Check : contact.icon"
									:size="16"
									:stroke-width="1.2"
								/>
							</button>
						</Popover.Trigger>
						<Popover.Positioner>
							<Popover.Content class="contact-menu">
								<a :href="contact.href" class="item" @click="handleContact">
									<AppIcon :name="contact.icon" :size="16" :stroke-width="1.2" />
									{{ $t(contact.actionLabel) }}
								</a>
								<button type="button" class="item" @click="copy(contact.key, contact.value)">
									<AppIcon :name="IconName.Copy" :size="16" :stroke-width="1.2" />
									{{ $t("callToActions.copyValue") }}
								</button>
							</Popover.Content>
						</Popover.Positioner>
					</Popover.Root>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue"
import { Popover } from "@ark-ui/vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"

export interface CollaborativeBroker {
	name: string
	email?: string
	phone?: string
	picture?: string
	title?: string
}

defineProps<{
	brokers: CollaborativeBroker[]
	mobile?: boolean
}>()

const emit = defineEmits<{
	contact: []
}>()

enum ContactField {
	Email = "email",
	Phone = "phone",
}

interface BrokerContact {
	key: string
	value: string
	href: string
	icon: IconName
	actionLabel: string
}

const openMenuKey = ref<string | null>(null)
const copiedKey = ref<string | null>(null)

function contactsOf(broker: CollaborativeBroker): BrokerContact[] {
	const contacts: BrokerContact[] = []
	if (broker.email) {
		contacts.push({
			key: `${broker.name}-${ContactField.Email}`,
			value: broker.email,
			href: `mailto:${broker.email}`,
			icon: IconName.MailSmall,
			actionLabel: "opportunities.emailAction",
		})
	}
	if (broker.phone) {
		contacts.push({
			key: `${broker.name}-${ContactField.Phone}`,
			value: broker.phone,
			href: `tel:${broker.phone}`,
			icon: IconName.Phone,
			actionLabel: "opportunities.callAction",
		})
	}
	return contacts
}

function handleContact(): void {
	openMenuKey.value = null
	emit("contact")
}

async function copy(key: string, value: string): Promise<void> {
	handleContact()
	await navigator.clipboard.writeText(value)
	copiedKey.value = key
	setTimeout(() => {
		if (copiedKey.value === key) copiedKey.value = null
	}, 2000)
}
</script>

<style scoped>
.collaborative-broker-list {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xs);
	padding: var(--spacing-sm) var(--spacing-md);
	border-top: var(--border-width-thin) solid var(--neutral-200);
	background: var(--neutral-0);
	font-family: var(--font-family-primary);

	&.mobile {
		padding-right: var(--spacing-sm);
	}

	.heading {
		color: var(--neutral-600);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		margin-bottom: var(--spacing-xs);
	}

	.broker {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-sm);

		& + .broker {
			margin-top: var(--spacing-xs);
			padding-top: var(--spacing-sm);
			border-top: var(--border-width-thin) solid var(--neutral-100);
		}
	}

	.identity {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		min-width: 0;
	}

	.picture {
		flex-shrink: 0;
		width: var(--space-32);
		height: var(--space-32);
		border-radius: var(--border-radius-full);
		background: var(--neutral-100);
		object-fit: contain;
		object-position: 70% center;
	}

	.text {
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	.name {
		color: var(--neutral-900);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
	}

	.role {
		color: var(--neutral-600);
		font-size: var(--font-size-xxs);
	}

	.cta {
		flex-shrink: 0;
		min-width: 0;
		padding: var(--spacing-xxs) var(--spacing-sm);
		text-transform: uppercase;
		box-shadow: none;
	}

	.actions {
		display: flex;
		flex-shrink: 0;
		gap: var(--spacing-xxs);
	}

	.action {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--spacing-xs);
		border-radius: var(--border-radius-sm);
		border: none;
		background: none;
		color: var(--neutral-500);
		cursor: pointer;
		transition: color 0.2s ease;

		&:hover {
			background: var(--neutral-50);
			color: var(--primary-green);
		}

		&.copied {
			color: var(--primary-green);
		}
	}
}

.contact-menu {
	display: flex;
	flex-direction: column;
	width: max-content;
	padding: var(--spacing-xs);
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-sm);
	box-shadow: var(--shadow-md);
	z-index: 1000;

	.item {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		width: 100%;
		padding: var(--spacing-sm);
		border: none;
		border-radius: var(--border-radius-sm);
		background: none;
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		text-align: left;
		text-decoration: none;
		cursor: pointer;
		transition: color 0.2s ease;

		.app-icon {
			flex-shrink: 0;
			color: var(--neutral-500);
			transition: color 0.2s ease;
		}

		&:hover {
			color: var(--primary-green);

			.app-icon {
				color: var(--primary-green);
			}
		}
	}
}
</style>
